/**
 * AddressTagChips — read-only render of a CustomerAddress's tags.
 *
 * Shared by the address book (CustomerAddressBook) and the service address
 * picker so a holiday house looks the same wherever it shows up.
 *
 * The tone → class map is spelled out in full because Tailwind only sees class
 * names it can find as literals; building `bg-${tone}-500/15` at runtime
 * silently produces unstyled chips in a production build.
 */

import { addressTagLabel, addressTagTone, type AddressTagTone } from '@shared/domain/address-tags';

export const ADDRESS_TAG_TONE_CLASSES: Record<AddressTagTone, string> = {
  blue: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  cyan: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  violet: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  rose: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  slate: 'bg-white/10 text-white/70 border-white/15',
};

/** Class string for a stored tag slug. */
export function addressTagClasses(slug: string): string {
  return ADDRESS_TAG_TONE_CLASSES[addressTagTone(slug)];
}

interface Props {
  tags?: string[];
  /** Rendered inline with other badges — omit the wrapper's own gap handling. */
  className?: string;
}

export function AddressTagChips({ tags, className }: Props) {
  if (!tags || tags.length === 0) return null;
  return (
    <>
      {tags.map((tag) => (
        <span
          key={tag}
          className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded border ${addressTagClasses(tag)} ${className || ''}`}
        >
          {addressTagLabel(tag)}
        </span>
      ))}
    </>
  );
}
